/**
 * VoiceFeaturesSection — landing-page section showcasing the voice features.
 *
 * Defaults to 4 scenes with Match Day Live as the marquee (Phase 1):
 *   1. Match Day Live    — call match events hands-free during the game
 *   2. Voice Coach Journal — capture observations anywhere, auto-routed
 *   3. AI Training Plan — describe a session, get a structured plan
 *   4. Schedule by Voice — say when, where, and what; session created
 *
 * Content is editable via the CMS (Voice Features tab in /super-admin).
 * Falls back to defaults when no CMS content is set.
 *
 * Pure CSS/SVG demo (no GIF). Respects prefers-reduced-motion. Pauses on hover.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Badge } from '../ui/badge';
import { Mic, Sparkles, CheckCircle2, Headphones, Zap, Shield, Trophy, ListOrdered } from 'lucide-react';
import { getSectionBg } from './sectionChrome';

const ICON_BY_KEY = {
  trophy: Trophy,
  headphones: Headphones,
  sparkles: Sparkles,
  mic: Mic,
  zap: Zap,
  list: ListOrdered,
};

const ACCENT_CLASSES = {
  emerald: { ring: 'ring-emerald-400/40', bg: 'bg-emerald-500', text: 'text-emerald-600', border: 'border-emerald-200', soft: 'bg-emerald-50' },
  amber:   { ring: 'ring-amber-400/40',   bg: 'bg-amber-500',   text: 'text-amber-600',   border: 'border-amber-200',   soft: 'bg-amber-50' },
  rose:    { ring: 'ring-rose-400/40',    bg: 'bg-rose-500',    text: 'text-rose-600',    border: 'border-rose-200',    soft: 'bg-rose-50' },
  blue:    { ring: 'ring-blue-400/40',    bg: 'bg-blue-500',    text: 'text-blue-600',    border: 'border-blue-200',    soft: 'bg-blue-50' },
  brand:   { ring: 'ring-[#007a33]/40',   bg: 'bg-[#007a33]',   text: 'text-[#007a33]',   border: 'border-[#007a33]/30', soft: 'bg-[#007a33]/10' },
};

const BADGE_CLASSES = {
  blue:    'bg-blue-100 text-blue-800 border-blue-200',
  amber:   'bg-amber-100 text-amber-800 border-amber-200',
  rose:    'bg-rose-100 text-rose-800 border-rose-200',
  emerald: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  brand:   'bg-[#007a33]/10 text-[#007a33] border-[#007a33]/30',
};

const DEFAULT_HEADER = {
  badge: 'BUILT FOR COACHES IN MOTION',
  title: 'Hands-free. Touchline-ready.',
  subtitle: "Four voice features so you can keep your eyes on the kids and your hands on a brew. Long-press, talk, done.",
  trust_line: "Private & secure. Your voice never leaves your team's data.",
  footer_line: 'Free trial includes all voice features. No credit card required.',
};

export const DEFAULT_VOICE_SCENES = [
  {
    id: 'matchday',
    title: 'Match Day Live',
    tagline: "Call events hands-free during the match — goals, subs, yellows. No taps.",
    transcript: 'Goal for Jamie Smith, assist from Sam Walker, twenty-third minute.',
    badge: 'EVENT LOGGED',
    badgeColor: 'emerald',
    chips: ['⚽ Goal · Jamie', '🅰️ Assist · Sam', "23'"],
    icon: 'trophy',
    accent: 'emerald',
  },
  {
    id: 'journal',
    title: 'Voice Coach Journal',
    tagline: 'Capture a coaching thought in 5 seconds — anywhere.',
    transcript: "Jamie's first touch was exceptional today. Push him into the No.10 role next session.",
    badge: 'PLAYER PROFILE',
    badgeColor: 'blue',
    chips: ['player feedback', 'tactical'],
    icon: 'headphones',
    accent: 'blue',
  },
  {
    id: 'plan',
    title: 'AI Training Plan',
    tagline: 'Describe the session — get a structured plan, ready for the pitch.',
    transcript: 'Hour-long finishing session for U12s, 12 players, focus on first touch under pressure.',
    badge: 'PLAN GENERATED',
    badgeColor: 'amber',
    chips: ['5 phases', '12 drills', '60 min'],
    icon: 'sparkles',
    accent: 'amber',
  },
  {
    id: 'session',
    title: 'Schedule by Voice',
    tagline: 'Say when, where, and what — your session is saved.',
    transcript: 'Training next Tuesday at 6pm at Hyde Park, pressing session for U12s.',
    badge: 'SESSION CREATED',
    badgeColor: 'rose',
    chips: ['Tue · 18:00', 'Hyde Park', 'AI plan attached'],
    icon: 'mic',
    accent: 'rose',
  },
];

const DEFAULT_SCENES = DEFAULT_VOICE_SCENES;

export const DEFAULT_VOICE_HEADER = DEFAULT_HEADER;

/* ---------- Section zoom-in (mirror FeaturesSection 'zoom' variant) ---------- */
function SlideInDemo({ children, disabled = false }) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(disabled);
  useEffect(() => {
    if (disabled) { setVisible(true); return undefined; }
    const el = ref.current;
    if (!el) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15, rootMargin: '0px 0px -60px 0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [disabled]);
  const style = disabled ? undefined : {
    transform: visible ? 'scale(1)' : 'scale(0.7)',
    opacity: visible ? 1 : 0,
    transformOrigin: 'center center',
    transition: 'transform 0.75s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.75s ease-out',
    willChange: 'transform, opacity',
  };
  return <div ref={ref} style={style}>{children}</div>;
}

/* ---------- Animated transcript (word-by-word reveal) ---------- */
function AnimatedTranscript({ text, isActive, durationMs = 2500, onComplete }) {
  const [visibleChars, setVisibleChars] = useState(0);
  const timerRef = useRef(null);
  const completedRef = useRef(false);
  useEffect(() => {
    completedRef.current = false;
    if (!isActive) { setVisibleChars(0); return undefined; }
    const start = performance.now();
    const tick = () => {
      const elapsed = performance.now() - start;
      const pct = Math.min(elapsed / durationMs, 1);
      setVisibleChars(Math.floor(text.length * pct));
      if (pct < 1) {
        timerRef.current = requestAnimationFrame(tick);
      } else if (!completedRef.current) {
        completedRef.current = true;
        if (typeof onComplete === 'function') onComplete();
      }
    };
    timerRef.current = requestAnimationFrame(tick);
    return () => { if (timerRef.current) cancelAnimationFrame(timerRef.current); };
  }, [isActive, text, durationMs, onComplete]);
  return (
    <span>
      “{text.slice(0, visibleChars)}
      {isActive && visibleChars < text.length && (
        <span className="inline-block w-0.5 h-4 bg-current align-middle animate-pulse ml-0.5" />
      )}
      ”
    </span>
  );
}

function MicWithWaveform({ accent }) {
  const cls = ACCENT_CLASSES[accent] || ACCENT_CLASSES.emerald;
  return (
    <div className="relative flex items-center justify-center" style={{ minWidth: '60px', minHeight: '60px' }}>
      <div className={`absolute inset-0 rounded-full ${cls.bg} opacity-20 animate-ping`} />
      <div className={`absolute inset-1 rounded-full ${cls.bg} opacity-30 animate-ping`} style={{ animationDelay: '0.3s' }} />
      <div className={`relative w-10 h-10 rounded-full ${cls.bg} flex items-center justify-center shadow-xl ring-4 ${cls.ring}`}>
        <Mic className="w-[18px] h-[18px] text-white" strokeWidth={1.75} />
      </div>
    </div>
  );
}

function ResultCard({ scene }) {
  const cls = ACCENT_CLASSES[scene.accent] || ACCENT_CLASSES.emerald;
  const badgeCls = BADGE_CLASSES[scene.badgeColor] || BADGE_CLASSES.blue;
  // Card is conditionally mounted by the parent only once the typing
  // animation completes (phase >= 3), so we drive the entrance animation
  // off a local "show" flag that flips on the next frame.
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = requestAnimationFrame(() => setShow(true));
    return () => cancelAnimationFrame(id);
  }, []);
  return (
    <div
      className={`rounded-2xl border ${cls.border} ${cls.soft} p-4 transition-all duration-500 ${
        show ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-2'
      }`}
    >
      <div className="flex items-center mb-2">
        <div className="flex items-center gap-2">
          <CheckCircle2 className={`w-5 h-5 ${cls.text}`} strokeWidth={2} />
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${badgeCls}`}>{scene.badge}</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-3">
        {(scene.chips || []).map((chip, idx) => (
          <span
            key={`${chip}-${idx}`}
            className={`text-xs px-2.5 py-1 rounded-full bg-white border ${cls.border} ${cls.text} font-medium transition-all duration-300`}
            style={{
              transitionDelay: show ? `${200 + idx * 100}ms` : '0ms',
              opacity: show ? 1 : 0,
              transform: show ? 'translateY(0)' : 'translateY(4px)',
            }}
          >
            {chip}
          </span>
        ))}
      </div>
    </div>
  );
}

/* Mobile status bar — mimics iOS look with signal / wifi / battery glyphs */
function PhoneStatusBar({ isDark, timeMode, customTime }) {
  const fg = isDark ? 'text-white' : 'text-gray-900';
  const [liveTime, setLiveTime] = useState(() =>
    new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
  );
  useEffect(() => {
    if (timeMode !== 'live') return undefined;
    const update = () => setLiveTime(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false }));
    update();
    const id = setInterval(update, 60_000);
    return () => clearInterval(id);
  }, [timeMode]);
  let display = '9:41';
  if (timeMode === 'live') display = liveTime;
  else if (timeMode === 'custom' && customTime) display = customTime;
  return (
    <div className={`flex items-center justify-between px-7 pt-3 pb-1 text-[11px] font-semibold ${fg}`}>
      <span className="tracking-tight" data-testid="phone-status-time">{display}</span>
      <div className="flex items-center gap-1.5">
        {/* signal bars */}
        <svg viewBox="0 0 18 12" className="w-4 h-3" aria-hidden="true">
          <rect x="0" y="8" width="3" height="4" rx="0.5" fill="currentColor" />
          <rect x="5" y="5" width="3" height="7" rx="0.5" fill="currentColor" />
          <rect x="10" y="2" width="3" height="10" rx="0.5" fill="currentColor" />
          <rect x="15" y="0" width="3" height="12" rx="0.5" fill="currentColor" />
        </svg>
        {/* wifi */}
        <svg viewBox="0 0 16 12" className="w-4 h-3" aria-hidden="true">
          <path d="M8 11.5a1 1 0 100-2 1 1 0 000 2zM8 8.5c1.1 0 2.1.4 2.9 1.1l1.4-1.4A6 6 0 008 6.5a6 6 0 00-4.3 1.7l1.4 1.4A4 4 0 018 8.5zM8 5c2 0 3.8.7 5.2 2l1.4-1.5A9 9 0 008 3a9 9 0 00-6.6 2.5L2.8 7A8 8 0 018 5z" fill="currentColor" />
        </svg>
        {/* battery */}
        <div className="flex items-center">
          <div className={`relative w-6 h-2.5 rounded-[3px] border ${isDark ? 'border-white/70' : 'border-gray-900/70'}`}>
            <div className={`absolute inset-0.5 rounded-[1.5px] ${isDark ? 'bg-white' : 'bg-gray-900'}`} style={{ width: '75%' }} />
          </div>
          <div className={`w-[2px] h-1.5 ml-px rounded-r-sm ${isDark ? 'bg-white/70' : 'bg-gray-900/70'}`} />
        </div>
      </div>
    </div>
  );
}

function ScenePlayer({ scene, isActive, theme, phoneWidth, phoneMinHeight, phoneBezelColor, phoneTimeMode, phoneTimeCustom }) {
  const [phase, setPhase] = useState(0);
  const timersRef = useRef([]);
  // Re-run whenever the scene itself changes too — without this the previous
  // scene's terminal phase (3) leaks into the new scene, so the result card
  // appears instantly before the new transcript starts typing.
  const sceneKey = scene?.id || scene?.title || '';
  useEffect(() => {
    timersRef.current.forEach(clearTimeout);
    timersRef.current = [];
    setPhase(0);
    if (!isActive) return undefined;
    timersRef.current.push(setTimeout(() => setPhase(1), 100));
    timersRef.current.push(setTimeout(() => setPhase(2), 600));
    // NOTE: phase 3 is no longer fired on a timer — it's triggered by the
    // AnimatedTranscript's onComplete callback (see below) so the result
    // card never appears until typing is fully finished, no matter how long
    // the configured transcript text is.
    return () => timersRef.current.forEach(clearTimeout);
  }, [isActive, sceneKey]);

  // Fired when the typing animation completes — wait 250ms then reveal the
  // result card so it feels like a deliberate "voice → captured" confirmation.
  const handleTranscriptComplete = useCallback(() => {
    const id = setTimeout(() => setPhase(3), 250);
    timersRef.current.push(id);
  }, []);

  const Icon = ICON_BY_KEY[scene.icon] || Mic;
  const isDark = theme === 'dark';
  const screenBg = isDark ? 'bg-gray-900' : 'bg-gray-50';
  const titleBarBg = isDark ? 'bg-gray-800/80' : 'bg-white/90';
  const transcriptBg = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';

  // Phone shell sizing — admin-configurable via CMS, with responsive defaults.
  const shellStyle = { borderRadius: '2.75rem' };
  if (phoneWidth && Number(phoneWidth) > 0) {
    shellStyle.width = `${phoneWidth}px`;
    shellStyle.maxWidth = '100%';
  } else {
    shellStyle.width = 'min(360px, 100%)';
  }
  if (phoneBezelColor) {
    shellStyle.backgroundColor = phoneBezelColor;
  }
  const screenStyle = { borderRadius: '2.1rem' };
  if (phoneMinHeight && Number(phoneMinHeight) > 0) {
    screenStyle.minHeight = `${phoneMinHeight}px`;
  } else {
    screenStyle.minHeight = '560px';
  }

  return (
    <div className="flex justify-center" data-testid="voice-features-phone-frame">
      {/* Phone outer shell — bezel, no drop shadow */}
      <div
        className="relative bg-gray-950 p-[10px] ring-1 ring-black/40"
        style={shellStyle}
      >
        {/* Side buttons — purely cosmetic */}
        <div className="absolute -left-[3px] top-24 w-[3px] h-10 rounded-l bg-gray-800" />
        <div className="absolute -left-[3px] top-40 w-[3px] h-16 rounded-l bg-gray-800" />
        <div className="absolute -left-[3px] top-60 w-[3px] h-16 rounded-l bg-gray-800" />
        <div className="absolute -right-[3px] top-32 w-[3px] h-20 rounded-r bg-gray-800" />

        {/* Screen */}
        <div
          className={`relative overflow-hidden ${screenBg}`}
          style={screenStyle}
        >
          {/* Dynamic island (circle — stays clear of status-bar icons at any phone width) */}
          <div className="absolute top-2.5 left-1/2 -translate-x-1/2 z-20 w-5 h-5 rounded-full bg-black" aria-hidden="true" />

          {/* Status bar */}
          <PhoneStatusBar isDark={isDark} timeMode={phoneTimeMode} customTime={phoneTimeCustom} />

          {/* App title bar */}
          <div className={`mx-3 mt-2 rounded-2xl ${titleBarBg} backdrop-blur-sm border ${isDark ? 'border-gray-800' : 'border-gray-200'} px-4 py-2.5 flex items-center gap-2`}>
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${(ACCENT_CLASSES[scene.accent] || ACCENT_CLASSES.emerald).bg}`}>
              <Icon className="w-4 h-4 text-white" strokeWidth={2} />
            </div>
            <span className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{scene.title}</span>
          </div>

          {/* App body */}
          <div className="px-5 pt-6 pb-10 space-y-4">
            <div className="flex justify-center pt-2"><MicWithWaveform accent={scene.accent} /></div>
            <div className={`rounded-2xl ${transcriptBg} border p-4 min-h-[80px] flex items-center justify-center`}>
              <p className={`text-sm text-center leading-relaxed ${isDark ? 'text-gray-100' : 'text-gray-700'}`}>
                <AnimatedTranscript text={scene.transcript} isActive={phase >= 2} durationMs={2500} onComplete={handleTranscriptComplete} />
              </p>
            </div>
            {phase >= 3 && <ResultCard key={scene.id} scene={scene} />}
          </div>

          {/* Home indicator bar */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 rounded-full bg-gray-500/70" aria-hidden="true" />
        </div>
      </div>
    </div>
  );
}

/* ---------- Section ---------- */
export function VoiceFeaturesSection({ content, theme, t }) {
  const sectionId = 'voice_features';
  const bgColor = getSectionBg(content, sectionId, theme);
  const isDark = theme === 'dark';

  // Pull CMS-editable content with fallbacks to defaults
  const header = { ...DEFAULT_HEADER, ...(content.voice_features_header || {}) };
  // Per-theme accent palette: when set to 'brand', any emerald accents in
  // scenes are remapped to the Grassroots brand green (#007a33). Other
  // explicit accents (blue / amber / rose) stay untouched so admin variety
  // is preserved.
  const activePalette = (theme === 'dark'
    ? (header.accent_palette_dark || 'emerald')
    : (header.accent_palette_light || 'emerald'));
  const remapEmerald = (s) => {
    if (!s || activePalette === 'emerald') return s;
    return {
      ...s,
      accent: s.accent === 'emerald' ? activePalette : s.accent,
      badgeColor: s.badgeColor === 'emerald' ? activePalette : s.badgeColor,
    };
  };
  const rawScenes = (Array.isArray(content.voice_features) && content.voice_features.length > 0)
    ? content.voice_features
    : DEFAULT_SCENES;
  const scenes = rawScenes.map(remapEmerald);
  const animationDisabled = content.voice_features_header?.animation_disabled === true;

  const [activeIdx, setActiveIdx] = useState(0);
  const [hovered, setHovered] = useState(false);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (hovered) return undefined;
    const reduceMotion = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) return undefined;
    intervalRef.current = setInterval(() => setActiveIdx(i => (i + 1) % scenes.length), 8500);
    return () => clearInterval(intervalRef.current);
  }, [hovered, scenes.length]);

  // Reset active index if scene array shrinks
  useEffect(() => {
    if (activeIdx >= scenes.length) setActiveIdx(0);
  }, [scenes.length, activeIdx]);

  const active = scenes[activeIdx] || scenes[0];

  return (
    <section
      id="voice_features"
      data-section="voice_features"
      className="relative pb-20 px-4 sm:px-6 lg:px-8"
      style={{ paddingTop: '5rem', backgroundColor: bgColor }}
      data-testid="landing-voice-features-section"
    >
      <div className="max-w-7xl mx-auto relative">
        {/* Section header — matches FeaturesSection treatment */}
        <div className="text-center mb-16">
          {header.badge && (
            <Badge className="bg-[#007a33] text-white border-[#007a33] mb-4 px-4 py-1 hover:bg-[#007a33] text-[0.7rem] font-black tracking-[1px]">
              {header.badge}
            </Badge>
          )}
          {header.title && (
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">{header.title}</h2>
          )}
          {header.subtitle && (
            <p className={`${t.textSecondary} text-lg max-w-3xl mx-auto`}>{header.subtitle}</p>
          )}
        </div>

        {/* Demo + feature pillars */}
        <div
          className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-6 items-center"
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {/* Animated demo (left, takes 3 cols) */}
          <div className="lg:col-span-3" data-testid="landing-voice-demo">
            <SlideInDemo disabled={animationDisabled}>
              <ScenePlayer
                scene={active}
                isActive
                theme={theme}
                phoneWidth={header.phone_width_px}
                phoneMinHeight={header.phone_min_height_px}
                phoneBezelColor={header.phone_bezel_color}
                phoneTimeMode={header.phone_time_mode}
                phoneTimeCustom={header.phone_time_custom}
              />
            </SlideInDemo>
            {/* Scene dots */}
            <div className="flex items-center justify-center gap-2 mt-4">
              {scenes.map((s, i) => (
                <button
                  key={s.id || i}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  className={`h-1.5 rounded-full transition-all ${
                    i === activeIdx
                      ? `w-10 ${(ACCENT_CLASSES[active.accent] || ACCENT_CLASSES.emerald).bg}`
                      : 'w-3 bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Show ${s.title} demo`}
                  data-testid={`landing-voice-dot-${i}`}
                />
              ))}
            </div>
          </div>

          {/* Feature pillars (right, takes 2 cols) */}
          <div className="lg:col-span-2 space-y-4">
            {scenes.map((s, i) => {
              const cls = ACCENT_CLASSES[s.accent] || ACCENT_CLASSES.emerald;
              const Icon = ICON_BY_KEY[s.icon] || Mic;
              const isActiveScene = i === activeIdx;
              return (
                <button
                  key={s.id || i}
                  type="button"
                  onClick={() => setActiveIdx(i)}
                  className={`w-full text-left rounded-2xl border p-4 transition-all ${
                    isActiveScene
                      ? `${cls.border} ${cls.soft} shadow-md scale-[1.02]`
                      : isDark
                        ? 'border-gray-600 bg-gray-700/60 hover:border-gray-500'
                        : 'border-gray-200 bg-gray-50 shadow-sm hover:border-gray-300'
                  }`}
                  data-testid={`landing-voice-pillar-${s.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-xl ${isActiveScene ? cls.bg : (isDark ? 'bg-gray-700' : 'bg-gray-100')} flex items-center justify-center flex-shrink-0`}>
                      <Icon className={`w-5 h-5 ${isActiveScene ? 'text-white' : (isDark ? 'text-gray-400' : 'text-gray-500')}`} strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className={`font-bold text-base mb-1 ${isActiveScene ? cls.text : t.text}`}>{s.title}</p>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{s.tagline}</p>
                    </div>
                  </div>
                </button>
              );
            })}

            {/* Trust mini-strip */}
            {header.trust_line && (
              <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'} pt-2`}>
                <Shield className="w-4 h-4 flex-shrink-0" />
                <span>{header.trust_line}</span>
              </div>
            )}
          </div>
        </div>

        {/* Bottom CTA line */}
        {header.footer_line && (
          <div className="mt-12 text-center">
            <p className={`text-sm ${t.textSecondary}`}>{header.footer_line}</p>
          </div>
        )}
      </div>
    </section>
  );
}

export default VoiceFeaturesSection;
